'use client'

import type { UseCarouselProps } from '@pearlconvert/ui/components/carousel/carousel.types'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useBreakpoint } from '@pearlconvert/ui/providers/breakpoint/breakpoint.context'

const getSlideIndex = (
  viewIndex: number,
  slidesPerView: number,
  slideCount: number
) => {
  const slideIndex = viewIndex * Math.floor(slidesPerView)
  return slideIndex > slideCount ? slideCount : slideIndex
}

export const useCarousel = ({
  slideCount,
  currentSlideIndex,
  snapToGrid,
  onSlideChange,
  trackRecommendationNavClick,
}: UseCarouselProps) => {
  const [slideIndex, setSlideIndex] = useState(currentSlideIndex || 0)
  // slidesPerView can be fractional
  const [slidesPerView, setSlidesPerView] = useState<number>(1)
  const [isLoading, setIsLoading] = useState(true)
  const { isMobileView } = useBreakpoint()
  const isMobile = isMobileView()

  const slidesRef = useRef<(HTMLLIElement | null)[]>([])
  const slidesContainerRef = useRef<HTMLUListElement>(null)
  const carouselWrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const countSPV = () => {
      const wrapperWidth = carouselWrapperRef.current?.offsetWidth || 0
      const firstSlide = slidesRef.current[0]
      const secondSlide = slidesRef.current[1]
      let slideWidth = 0
      let gap = 0
      if (firstSlide && secondSlide) {
        slideWidth = firstSlide.offsetWidth
        gap = secondSlide.offsetLeft - firstSlide.offsetLeft - slideWidth
      } else {
        slideWidth = firstSlide?.offsetWidth || 0
      }

      return slideWidth > 0 ? (wrapperWidth + gap) / (slideWidth + gap) : 1
    }

    // Read SPV value from the CSS variable. If it's 'auto', calculate it from container and slide widths
    const updateSlidesPerView = () => {
      if (slidesContainerRef.current) {
        const style = getComputedStyle(slidesContainerRef.current)
        const value = style
          .getPropertyValue('--carousel-slides-per-view')
          .trim()

        if (value === 'auto') {
          setSlidesPerView(countSPV())
          setIsLoading(false)
        } else {
          const numValue = Number(value)
          if (numValue > 0) {
            setSlidesPerView(numValue)
            setIsLoading(false)
          }
        }
      }
    }
    // Defer the initial measurement (getComputedStyle + offsetWidth/offsetLeft)
    // to the next frame so it doesn't force a synchronous reflow during the
    // mount commit. Multiple carousels then batch their reads into one frame.
    const rafId = requestAnimationFrame(updateSlidesPerView)
    window.addEventListener('resize', updateSlidesPerView)
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateSlidesPerView)
    }
  }, [])

  // View windows count. Partially visible slides don’t count into the view
  const viewCount = Math.ceil(slideCount / Math.floor(slidesPerView))
  // Which view window is currently active
  const viewIndex = Math.floor(slideIndex / Math.floor(slidesPerView))

  const isAutoScrollingRef = useRef(false)

  const scrollToSlide = useCallback((index: number) => {
    const slidesContainer = slidesContainerRef.current
    if (!slidesContainer) {
      return
    }

    isAutoScrollingRef.current = true
    setSlideIndex(index)

    slidesContainer.scrollTo({
      left: slidesRef.current[index]?.offsetLeft || 0,
      behavior: 'smooth',
    })
  }, [])

  const updateViewIndex = useCallback(
    (viewIndex: number) => {
      const boundedIndex = Math.max(0, Math.min(viewIndex, viewCount - 1))
      const newSlideIndex = getSlideIndex(
        boundedIndex,
        slidesPerView,
        slideCount
      )
      scrollToSlide(newSlideIndex)
      trackRecommendationNavClick?.()
    },
    [
      viewCount,
      slidesPerView,
      slideCount,
      scrollToSlide,
      trackRecommendationNavClick,
    ]
  )

  const handleScroll = useCallback(() => {
    if (isAutoScrollingRef.current) {
      return
    }

    const slidesContainer = slidesContainerRef.current
    if (!slidesContainer || slidesRef.current.length === 0) {
      return
    }

    const { scrollLeft, scrollWidth, clientWidth } = slidesContainer

    if (scrollLeft <= 0) {
      setSlideIndex(0)
      onSlideChange?.(0)
      return
    }

    // -1 to account for rounding errors. Otherwise, the result is browser-width-sensitive
    if (scrollLeft >= scrollWidth - clientWidth - 1) {
      setSlideIndex(slideCount - 1)
      onSlideChange?.(slideCount - 1)
      return
    }

    const slideOffsets = slidesRef.current.map(
      (slide) => slide?.offsetLeft || 0
    )

    const closestIndex = slideOffsets.reduce((prev, curr, index) =>
      Math.abs(curr - scrollLeft) < Math.abs(slideOffsets[prev] - scrollLeft)
        ? index
        : prev
    )

    setSlideIndex(closestIndex)
    onSlideChange?.(closestIndex)
  }, [onSlideChange, slideCount])

  const handleScrollEnd = useCallback(() => {
    isAutoScrollingRef.current = false
  }, [])

  useEffect(() => {
    const slidesContainer = slidesContainerRef.current
    if (!slidesContainer) {
      return
    }

    slidesContainer.addEventListener('scroll', handleScroll)
    slidesContainer.addEventListener('scrollend', handleScrollEnd)

    return () => {
      slidesContainer.removeEventListener('scroll', handleScroll)
      slidesContainer.removeEventListener('scrollend', handleScrollEnd)
    }
  }, [handleScroll, handleScrollEnd])

  useEffect(() => {
    if (currentSlideIndex !== undefined) {
      setSlideIndex(currentSlideIndex)
      onSlideChange?.(currentSlideIndex)
    }
  }, [currentSlideIndex, onSlideChange])

  useEffect(() => {
    if (!snapToGrid || !slidesContainerRef.current) {
      return
    }

    ;[...slidesContainerRef.current.children].forEach((slide, index, array) => {
      if (index % Math.floor(slidesPerView) === 0) {
        slide?.classList.add('snap')
      } else {
        slide?.classList.remove('snap')
      }
      if (index === array.length - 1) {
        slide?.classList.add('snap-end')
      }
    })
  }, [isMobile, slidesPerView, snapToGrid])

  return {
    carouselWrapperRef,
    isLoading,
    slideIndex,
    viewCount,
    viewIndex,
    updateViewIndex,
    slidesRef,
    slidesContainerRef,
    slidesPerView,
  }
}
